import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Mail, Phone, X, CheckCircle2, BookOpen } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './FloatingContactButton.css';

const FloatingContactButton = () => {
  const email = process.env.REACT_APP_CONTACT_EMAIL || 'fielderusa@gmail.com';
  const phoneRaw = process.env.REACT_APP_CONTACT_PHONE;

  const phone = useMemo(() => (phoneRaw ? String(phoneRaw).trim() : ''), [phoneRaw]);
  const [isOpen, setIsOpen] = useState(false);
  const [didCopyEmail, setDidCopyEmail] = useState(false);
  const panelRef = useRef(null);

  // Handle outside click & escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => e.key === 'Escape' && setIsOpen(false);
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setIsOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle copy state reset
  useEffect(() => {
    if (didCopyEmail) {
      const timer = setTimeout(() => setDidCopyEmail(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [didCopyEmail]);

  const handleCopyEmail = async () => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = email;
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setDidCopyEmail(true);
  };

  return (
    <div className="fielder-contact" aria-live="polite">
      <div ref={panelRef} className={`fielder-contact-panel ${isOpen ? 'open' : ''}`}>
        
        {/* Toggle Button */}
        <button
          type="button"
          className="fielder-contact-fab"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Close support options' : 'Open support options'}
        >
          {isOpen ? <X size={20} /> : <MessageCircle size={20} />}
          <span className="fielder-contact-fab-text">Support</span>
        </button>

        {/* Popup Card */}
        {isOpen && (
          <div className="card fielder-contact-popup">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3 text-dark">Need assistance?</h6>
              <div className="d-flex flex-column gap-2">
                
                {email && (
                  <button
                    type="button"
                    className="btn rounded-pill fw-medium d-flex align-items-center justify-content-center gap-2 fielder-contact-action"
                    onClick={handleCopyEmail}
                  >
                    {didCopyEmail ? (
                      <CheckCircle2 size={16} className="text-success" />
                    ) : (
                      <Mail size={16} />
                    )}
                    <span>{didCopyEmail ? 'Email copied!' : email}</span>
                  </button>
                )}

                {phone && (
                  <a
                    href={`tel:${phone}`}
                    className="btn rounded-pill fw-medium d-flex align-items-center justify-content-center gap-2 fielder-contact-action text-decoration-none"
                  >
                    <Phone size={16} />
                    <span>{phone}</span>
                  </a>
                )}

                <Link
                  to="/help"
                  className="btn rounded-pill fw-medium d-flex align-items-center justify-content-center gap-2 fielder-contact-action text-decoration-none"
                >
                  <BookOpen size={16} />
                  <span>Help Center</span>
                </Link>

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FloatingContactButton;